import { useCallback, useMemo, useState } from "react";
import type { DocumentNode } from "@apollo/client";
import type { SortingState } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, {
  TableHeading,
  TableControls,
  toTableControlActions,
  buildExpandableCountColumn,
  buildActionColumn,
  buildIconTextColumn,
  buildCopyTextColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { calculateMonthsBetween, toFormat, isInFuture } from "@/lib/dateHelpers";
import Icon from "@/components/Icon";
import Tooltip from "@/components/Tooltip";
import { useIsCareerServicesBool } from "@/hooks/useCapabilities";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import NotesTable from "@/features/global/components/NotesTable";
import { NoteNotableValues, WorkingLocation } from "@/types";
import type { EmploymentRow } from "@/pages/people/[pgaId]/person/queries";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { filterByClientSearch } from "@/lib/clientSearch";

const NO_END_DATE_LABEL = "Present";

const futureDateWarning = (text: string) => ({
  name: "emergency_home" as const,
  size: 20,
  className: "text-extended-warning-brand-color",
  tooltip: { supportingText: text }
});

function formatWorkingLocation(value: WorkingLocation | null | undefined): string {
  if (!value) return "—";
  switch (value) {
    case "ON_SITE":
      return "On-site";
    case "REMOTE":
      return "Remote";
    default:
      return "—";
  }
}

function formatTimeEmployed(
  startedOn: string | null | undefined,
  endedOn: string | null | undefined,
  showYears: boolean
): string {
  if (!startedOn || isInFuture(startedOn)) return "—";
  const effectiveEnd = endedOn && !isInFuture(endedOn) ? endedOn : new Date();
  const months = calculateMonthsBetween(startedOn, effectiveEnd);
  if (months <= 0) return "0.0 months";
  if (!showYears || months < 12) return `${months.toFixed(1)} months`;

  const years = Math.floor(months / 12);
  const remainderMonths = Math.floor(months - years * 12);
  const yearLabel = years === 1 ? "year" : "years";
  const monthLabel = remainderMonths === 1 ? "month" : "months";
  return `${years} ${yearLabel}, ${remainderMonths} ${monthLabel}`;
}

function formatTableData(list: EmploymentRow[], showYears: boolean) {
  return list.map((e) => {
    const timeEmployed = formatTimeEmployed(e.startedOn, e.endedOn, showYears);

    return {
      id: e.id,
      facilityId: e.facility.id,
      facilityName: e.facility.name,
      sectionName:
        e.workingLocation === WorkingLocation.REMOTE && e.affiliationSection?.name
          ? e.affiliationSection.name
          : e.facility.section?.name,
      classification: e.jobClassification,
      jobTitle: e.formattedTitle || e.title,
      workingLocation: formatWorkingLocation(e.workingLocation),
      startDate: e.startedOn ? toFormat(e.startedOn) : "—",
      startDateRaw: e.startedOn,
      endDate: e.endedOn ? toFormat(e.endedOn) : NO_END_DATE_LABEL,
      endDateRaw: e.endedOn,
      timeEmployed,
      isPrimary: e.isPrimary,
      employmentKind: e.employmentKind,
      notes: e.notes || []
    };
  });
}

type FormattedEmployment = ReturnType<typeof formatTableData>[0];

export interface DeleteEmploymentParams {
  employmentId: string;
  facilityName?: string;
  jobTitle?: string;
  startDate?: string;
  endDate?: string;
}

export interface EmploymentTableProps {
  employments: EmploymentRow[];
  loading: boolean;
  canEdit: boolean;
  canAddEmployment?: boolean;
  pgaId: string;
  onViewFacility: (facilityId: string) => void;
  onClickEmployment: (employmentId: string) => void;
  onAddEmployment?: () => void;
  onEditEmployment?: (employmentId: string) => void;
  canEditEmployment?: (employment: FormattedEmployment) => boolean;
  canManageEmployment?: (employment: FormattedEmployment) => boolean;
  onDeleteEmployment?: (params: DeleteEmploymentParams) => void;
  onAddNote?: (employmentId: string) => void;
  refetchQueries: DocumentNode[];
  highlightEmploymentId?: string | null;
  highlightNoteId?: string | null;
  tableRef?: React.Ref<HTMLDivElement>;
  showHeading?: boolean;
}

export default function EmploymentTable({
  employments,
  loading,
  canEdit,
  canAddEmployment = canEdit,
  pgaId,
  onViewFacility,
  onClickEmployment,
  onAddEmployment,
  onEditEmployment,
  canEditEmployment,
  canManageEmployment,
  onDeleteEmployment,
  onAddNote,
  refetchQueries,
  highlightEmploymentId,
  highlightNoteId,
  tableRef,
  showHeading = true
}: EmploymentTableProps) {
  const { Menu, setMenu } = useTableRowMenu();
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "endDate", desc: true }]);
  const isCareerServices = useIsCareerServicesBool();
  const { shouldShowFacilityProfileActions } = useUniversityStaffPermissions();
  const { searchValue, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "employmentSearch"
  });
  const hasSearch = !!searchValue.trim();

  const tableData = useMemo(
    () => (employments.length ? formatTableData(employments, isCareerServices) : []),
    [employments, isCareerServices]
  );
  const visibleRows = useMemo(
    () =>
      filterByClientSearch(tableData, searchValue, (row) => [
        row.facilityName,
        row.sectionName,
        row.classification,
        row.jobTitle,
        row.workingLocation,
        row.startDate,
        row.endDate,
        row.timeEmployed
      ]),
    [searchValue, tableData]
  );

  const canManageRow = useCallback(
    (employment: FormattedEmployment) =>
      (canManageEmployment ? canManageEmployment(employment) : canEdit) &&
      (!canEditEmployment || canEditEmployment(employment)),
    [canManageEmployment, canEdit, canEditEmployment]
  );

  const renderNotesPanel = useCallback(
    (row: { original: FormattedEmployment }) => {
      const notes = row.original.notes;
      if (notes.length === 0) {
        return <></>;
      }

      return (
        <NotesTable
          variant="details-panel"
          data={notes}
          title={`Notes (${notes.length})`}
          highlightNoteId={highlightNoteId}
          noteEditing={{
            enabled: canManageRow(row.original) && !!onAddNote,
            notableId: row.original.id,
            notableType: NoteNotableValues.EMPLOYMENT,
            refetchQueries,
            pgaId: pgaId || "",
            objectType: "person"
          }}
        />
      );
    },
    [highlightNoteId, canManageRow, pgaId, refetchQueries, onAddNote]
  );

  const headingActions = canAddEmployment
    ? [
        {
          label: "Add Employment",
          icon: "add" as const,
          variant: "filled" as const,
          onClick: () => onAddEmployment?.(),
          testId: "add-employment-button"
        }
      ]
    : [];

  return (
    <>
      <Menu />
      <div ref={tableRef}>
        {showHeading ? (
          <TableHeading
            heading="Employment Details"
            body="View employment history including past and present facilities and job titles below"
            actions={headingActions}
          />
        ) : (
          <div className="pt-12" />
        )}
        <Table
          data={visibleRows}
          loading={loading}
          onClickRow={(row) => onClickEmployment(row.original.id)}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          getRowCanExpand={(row) => (row.original.notes?.length || 0) > 0}
          renderExpandedRow={({ row }) => renderNotesPanel(row)}
          hasActiveFilters={hasSearch}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              actions={!showHeading ? toTableControlActions(headingActions) : []}
              onSortClick={onSortClick}
            >
              <SearchInput
                placeholder="Search Employments"
                query={searchValue}
                onChange={setSearchValue}
                onClear={clearSearch}
              />
            </TableControls>
          )}
          columns={[
            buildCopyTextColumn<FormattedEmployment>({
              accessorKey: "facilityName",
              header: "Facility Name",
              copyFieldLabel: "Facility Name",
              size: 250,
              enableSorting: false,
              getText: (row) => row.facilityName,
              getSecondaryText: (row) => row.sectionName,
              shouldShowCopyButton: (row) => !!row.facilityName,
              getPrefix: (row) =>
                row.isPrimary ? (
                  <Tooltip variant="plain" supportingText="Primary" color="black">
                    <Icon name="check" size={24} />
                  </Tooltip>
                ) : null,
              meta: { headerClassName: "hide-header-in-column" }
            }),
            {
              header: "Employed As",
              size: 140,
              id: "classification",
              accessorKey: "classification",
              enableSorting: false
            },
            {
              header: "Job Title",
              size: 250,
              id: "jobTitle",
              accessorKey: "jobTitle",
              enableSorting: false
            },
            {
              header: "Working Location",
              size: 150,
              id: "workingLocation",
              accessorKey: "workingLocation",
              enableSorting: false
            },
            buildIconTextColumn<FormattedEmployment>({
              accessorKey: "startDate",
              header: "Start Date",
              enableSorting: false,
              getText: (row) => row.startDate,
              getIcon: (row) =>
                row.startDateRaw && isInFuture(row.startDateRaw)
                  ? futureDateWarning("Start date is in the future")
                  : null
            }),
            {
              ...buildIconTextColumn<FormattedEmployment>({
                accessorKey: "endDate",
                header: "End Date",
                sortType: "date",
                getText: (row) => {
                  const isFutureStart = row.startDateRaw && isInFuture(row.startDateRaw);
                  if (!row.endDateRaw && isFutureStart) return null;
                  return row.endDate;
                },
                getIcon: (row) => {
                  const isFutureStart = row.startDateRaw && isInFuture(row.startDateRaw);
                  const isFutureEnd = row.endDateRaw && isInFuture(row.endDateRaw);
                  if ((!row.endDateRaw && isFutureStart) || isFutureEnd)
                    return futureDateWarning("End date is in the future");
                  return null;
                }
              }),
              sortingFn: (rowA, rowB) => {
                const dateA = rowA.original.endDateRaw || "9999-12-31";
                const dateB = rowB.original.endDateRaw || "9999-12-31";
                return dateA.localeCompare(dateB);
              }
            },
            {
              header: "Time Employed",
              size: 180,
              id: "timeEmployed",
              accessorKey: "timeEmployed",
              enableSorting: false
            },
            buildExpandableCountColumn<FormattedEmployment>({
              accessorKey: "notes",
              header: "Notes",
              size: 100,
              getCount: (row) => row.notes?.length || 0,
              showExpandIcon: true,
              autoExpandRowId: highlightEmploymentId
            }),
            buildActionColumn<FormattedEmployment>({
              getActions: (row) => {
                const isActiveEmployment =
                  row.endDate === NO_END_DATE_LABEL && row.startDateRaw && !isInFuture(row.startDateRaw);

                const actions: Action[] = [];

                const canManage = canManageRow(row);

                if (canManage && onEditEmployment) {
                  actions.push({
                    section: "Manage",
                    text: "Edit Employment",
                    onClick: () => onEditEmployment(row.id)
                  });
                }

                if (shouldShowFacilityProfileActions) {
                  actions.push({
                    section: "Actions",
                    text: "View Facility",
                    onClick: () => onViewFacility(row.facilityId)
                  });
                }

                if (canManage && onAddNote) {
                  actions.push({
                    section: "Actions",
                    text: "Add Note",
                    onClick: () => onAddNote(row.id)
                  });
                }

                if (canManage && onDeleteEmployment && !isActiveEmployment) {
                  actions.push({
                    text: "Delete Employment",
                    iconName: "delete",
                    destructive: true,
                    onClick: () =>
                      onDeleteEmployment({
                        employmentId: row.id,
                        facilityName: row.facilityName,
                        jobTitle: row.jobTitle,
                        startDate: row.startDate ?? undefined,
                        endDate: row.endDate ?? undefined
                      })
                  });
                }

                return actions;
              },
              onViewClick: (row) => onClickEmployment(row.id),
              showViewButton: () => true,
              viewButtonTitle: "View Facility",
              actionId,
              setMenu
            })
          ]}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Employments"
              caption="This person does not have any known employments"
              iconName="business_center"
            />
          )}
          renderFilteredEmptyState={() => (
            <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
          )}
          data-testid="employment-details-table"
        />
      </div>
    </>
  );
}
